// Pure helpers behind the skill editor and upload flows. Zero imports: node-tested.

/** Files in the order a reader expects: manifest, then the method body, then references, then the rest. */
export function orderedPaths(paths: readonly string[]): string[] {
  const rank = (p: string): number => {
    if (p === 'manifest.json') return 0;
    if (p === 'SKILL.md' || p === 'method.md') return 1;
    if (p === 'README.md') return 2;
    if (p.startsWith('references/')) return 3;
    return 4;
  };
  return [...paths].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * A picked folder arrives as paths like "ad-budget-pacing/references/x.md". Strip the common top
 * directory so the map is relative to the skill directory, and report that directory's name.
 * Files outside any folder (a multi-file pick) keep their names as-is.
 */
export function relativizeFolder(entries: ReadonlyArray<{ path: string; text: string }>): { dirName: string | null; files: Record<string, string> } {
  const files: Record<string, string> = {};
  const tops = new Set(entries.map((e) => (e.path.includes('/') ? e.path.split('/')[0]! : '')));
  const strip = tops.size === 1 && !tops.has('');
  const dirName = strip ? [...tops][0]! : null;
  for (const e of entries) {
    const rel = strip ? e.path.slice(dirName!.length + 1) : e.path;
    if (rel && !rel.split('/').some((seg) => seg.startsWith('.'))) files[rel] = e.text;
  }
  return { dirName, files };
}

/** A safe directory name from whatever the folder was called: "Ad Budget Pacing" → "ad-budget-pacing". */
export function slugifyDirName(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
}

export const CHANGE_KIND_LABELS: Record<'edit' | 'upload' | 'create', string> = {
  edit: 'Edit',
  upload: 'New version',
  create: 'New skill',
};

export const CHANGE_STATUS_LABELS: Record<'pr_open' | 'merged' | 'closed', string> = {
  pr_open: 'PR open',
  merged: 'Merged · live',
  closed: 'Closed',
};

/** True when the text is a JSON document with no syntax error (the editor blocks a save otherwise). */
export function jsonError(text: string): string | null {
  try {
    JSON.parse(text);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

/** Which files changed between what was loaded and what the editor holds. */
export function changedPaths(before: Record<string, string>, after: Record<string, string>): string[] {
  const paths = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...paths].filter((p) => before[p] !== after[p]).sort();
}
